import { FC, useMemo } from 'react';

export type ParsedDataGridProps = {
  headers: string[];
  rows: Array<Record<string, any>>;
  rowErrorsById?: Record<string, string[]>;
  issueType?: 'error' | 'warning';
  className?: string;
};

const letterFromIndex = (index: number): string => {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

const TopLeftCorner: FC = () => (
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  <th className="sticky top-0 z-10 border-b border-r border-[#d6d6d6] bg-[#f0f0f0] p-2 text-left text-xs font-medium text-grey" />
);

const ColLetterHeaderCell: FC<{ label: string }> = ({ label }) => (
  <th className="sticky top-0 z-10 border-b border-r border-b-[#ababab] border-r-[#d6d6d6] bg-[#f0f0f0] px-3 py-2 text-center text-xs font-medium text-grey">
    {label}
  </th>
);

const RowIndexHeaderCell: FC<{ index: number }> = ({ index }) => (
  <th className="sticky left-0 z-0 border-b border-r border-b-[#d6d6d6] border-r-[#ababab] bg-[#f0f0f0] p-2 text-xs font-medium text-grey">
    {index}
  </th>
);

const DataHeaderCell: FC<{ value: string }> = ({ value }) => (
  <td className="whitespace-nowrap border-b border-r border-[#d6d6d6] px-3 py-2 text-sm font-medium text-content">
    {value}
  </td>
);

const DataCell: FC<{ value: any }> = ({ value }) => (
  <td className="whitespace-nowrap border-b border-r border-[#d6d6d6] px-3 py-2 text-sm text-content">
    {value as any}
  </td>
);

const ErrorCell: FC<{ errors?: string[] }> = ({ errors }) => (
  <td className="min-w-60 border-b border-r border-[#d6d6d6] px-3 py-2 align-top text-sm">
    {errors && errors.length ? (
      <div className="flex flex-col gap-1">
        {errors.map((msg) => (
          <div key={msg} className="text-red40 text-xs">
            {msg}
          </div>
        ))}
      </div>
    ) : (
      <span />
    )}
  </td>
);

const ParsedDataGrid: FC<ParsedDataGridProps> = ({
  headers,
  rows,
  rowErrorsById,
  issueType = 'error',
  className = ''
}) => {
  const safeHeaders = useMemo(() => headers || [], [headers]);
  const hasErrorColumn = useMemo(
    () => !!rowErrorsById && Object.keys(rowErrorsById).length > 0,
    [rowErrorsById]
  );

  const headerRow: Record<string, any> = useMemo(() => {
    const r: Record<string, any> = {};
    safeHeaders.forEach((h) => {
      r[h] = h;
    });
    // eslint-disable-next-line no-underscore-dangle
    (r as any).__rowId = 'header-row';
    return r;
  }, [safeHeaders]);

  const displayRows = useMemo(() => [headerRow, ...rows], [headerRow, rows]);
  const totalCols = safeHeaders.length + (hasErrorColumn ? 1 : 0);
  const errorBgColor =
    issueType === 'warning' ? 'bg-[#F59E0B1A]' : 'bg-[#F443361A]';

  return (
    <div
      className={`max-h-96 overflow-auto border-l border-[#0e7c41] ${className}`}
    >
      <table className="min-w-full table-fixed">
        <thead>
          <tr>
            <TopLeftCorner />
            {Array.from({ length: totalCols }).map((_, idx) => (
              <ColLetterHeaderCell
                // eslint-disable-next-line react/no-array-index-key
                key={`col-${idx}`}
                label={letterFromIndex(idx)}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row: Record<string, any>, rowIdx) => {
            // eslint-disable-next-line no-underscore-dangle
            const key = (row.__rowId as string) || JSON.stringify(row);
            const isHeader = key === 'header-row';
            const rowNumber = rowIdx + 1;
            const rowErrs = isHeader ? undefined : rowErrorsById?.[key];
            const hasErrors = !!rowErrs && rowErrs.length > 0;

            return (
              <tr
                key={key}
                className={`even:bg-grey5 ${
                  hasErrors ? errorBgColor : undefined
                }`}
              >
                <RowIndexHeaderCell index={rowNumber} />
                {safeHeaders.map((h) =>
                  isHeader ? (
                    <DataHeaderCell key={`${key}-${h}`} value={row[h] ?? ''} />
                  ) : (
                    <DataCell key={`${key}-${h}`} value={row[h] ?? ''} />
                  )
                )}
                {hasErrorColumn && <ErrorCell errors={rowErrs} />}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ParsedDataGrid;
